import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { CONTACT } from '@/config/contact';
import { useLocalizedPath } from '@/lib/locale';
import { Phone, Mail, MapPin, Facebook, Linkedin, Instagram, Youtube } from 'lucide-react';
import WebsiteCarbonBadge from '@/components/WebsiteCarbonBadge';

interface FooterProps {
  variant?: 'default' | 'compact';
}

export function Footer({ variant = 'default' }: FooterProps) {
  const { t } = useLanguage();
  const isCompact = variant === 'compact';
  const localizedPath = useLocalizedPath();
  
  return (
    <footer
      className={`bg-black/50 backdrop-blur-sm px-4 ${
        isCompact ? 'py-10' : 'py-16'
      }`}
    >
      <div className="container mx-auto">
        <div
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 ${
            isCompact ? 'mb-6' : 'mb-8'
          }`}
        >
          <div>
            <h3 className="text-xl font-bold text-white mb-4">PixelPro Solutions</h3>
            <p className="text-gray-300 mb-4">
              {t('footer.company.description')}
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/pixelpro.solutions/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href="https://www.linkedin.com/in/alex-alali/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href="https://www.instagram.com/pixelpro.solutions/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href="https://www.youtube.com/@PixelproSolutions" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors" aria-label="YouTube">
                <Youtube className="h-5 w-5" aria-hidden="true" />
              </a>
            </div>
            <WebsiteCarbonBadge className="mt-8" />
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-white mb-4">{t('footer.services.title')}</h3>
            <ul className="space-y-3">
              <li><Link to={localizedPath('/services/web-design')} className="text-gray-300 hover:text-white transition-colors">{t('footer.services.website')}</Link></li>
              <li><Link to={localizedPath('/services/ecommerce')} className="text-gray-300 hover:text-white transition-colors">{t('footer.services.ecommerce')}</Link></li>
              <li><Link to={localizedPath('/services/branding')} className="text-gray-300 hover:text-white transition-colors">{t('footer.services.uiux')}</Link></li>
              <li><Link to={localizedPath('/services/marketing-strategy')} className="text-gray-300 hover:text-white transition-colors">{t('footer.services.marketing')}</Link></li>
              <li><Link to={localizedPath('/services/apps')} className="text-gray-300 hover:text-white transition-colors">{t('footer.services.mobile')}</Link></li>
              <li><Link to={localizedPath('/services/content-production')} className="text-gray-300 hover:text-white transition-colors">{t('footer.services.content')}</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-white mb-4">{t('footer.links.title')}</h3>
            <ul className="space-y-3">
              <li><Link to={localizedPath('/')} className="text-gray-300 hover:text-white transition-colors">{t('footer.links.home')}</Link></li>
              <li><Link to={localizedPath('/about')} className="text-gray-300 hover:text-white transition-colors">{t('footer.links.about')}</Link></li>
              <li><Link to={localizedPath('/projects')} className="text-gray-300 hover:text-white transition-colors">{t('footer.links.projects')}</Link></li>
              <li><Link to={localizedPath('/articles')} className="text-gray-300 hover:text-white transition-colors">{t('footer.links.articles')}</Link></li>
              <li><Link to={localizedPath('/consultation')} className="text-gray-300 hover:text-white transition-colors">{t('footer.links.contact')}</Link></li>
              <li><Link to={localizedPath('/privacy-policy')} className="text-gray-300 hover:text-white transition-colors">{t('footer.links.privacy')}</Link></li>
              <li><Link to={localizedPath('/cookie-policy')} className="text-gray-300 hover:text-white transition-colors">{t('footer.links.cookies')}</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-white mb-4">{t('footer.contact.title')}</h3>
            <ul className="space-y-3">
              <li className="text-gray-300 font-semibold">PixelPro Solutions</li>
              <li className="text-gray-300 flex items-center gap-2">
                <MapPin className="h-4 w-4 flex-shrink-0" />
                <span>60100, Katerini, Greece</span>
              </li>
              <li className="text-gray-300 flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <a href={CONTACT.phoneHref} className="text-gray-100 hover:text-white transition-colors">
                  {CONTACT.phoneDisplay}
                </a>
              </li>
              <li className="text-gray-300 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <a href={`mailto:${CONTACT.email}`} className="text-gray-100 hover:text-white transition-colors">
                  {CONTACT.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-10">
          <div className="text-center space-y-2">
            <p className="text-gray-300">
              &copy; {new Date().getFullYear()} PixelPro Solutions. {t('footer.copyright')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
